import { useCallback, useEffect, useMemo, useState } from 'react';
import { Input } from '../ui/Input/Input';
import { PrimaryButton, WhiteButton } from '../ui/Button/Buttons';
import { useAppSelector } from '../../hooks/redux';
import { inviteUser, InviteUserRequest } from '../../services/TeamService';
import { EMAIL_REGEX } from '../../constants/Validation.constants';
import { useDispatch } from 'react-redux';
import {
  inviteUserFailure,
  inviteUserStart,
  inviteUserSuccess,
  setIsUserInvited
} from '../../store/slices/userManagementSlice';
import DropdownPortal from '../ui/DropdownPortal/DropdownPortal';
import { PlatformRole } from '../../types/Team.types';
import { getPlatformRoleLabel } from '../../utils/Functions.utils';

type InviteUserProps = Readonly<{
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  registeredUserEmail?: string;
  assignListForAiSystemFromUser?: unknown[];
  userRoles?: PlatformRole[];
  showCompanyInput?: boolean;
}>;

export function InviteUser({
  setIsOpen,
  registeredUserEmail,
  userRoles = [],
  showCompanyInput
}: InviteUserProps) {
  const { teamInvitations, teamMembers } = useAppSelector(
    (state) => state.userManagement
  );

  const [email, setEmail] = useState(registeredUserEmail || '');
  const [role, setRole] = useState<PlatformRole | ''>(
    userRoles.length === 1 ? userRoles[0] : ''
  );
  const [company, setCompany] = useState('');
  const [emailError, setEmailError] = useState<string>('');
  const [companyError, setCompanyError] = useState<string>('');
  const [touchedEmail, setTouchedEmail] = useState<boolean>(false);
  const [touchedCompany, setTouchedCompany] = useState<boolean>(false);
  const dispatch = useDispatch();

  const roleOptions = useMemo(
    () =>
      userRoles.map((value) => ({ value, label: getPlatformRoleLabel(value) })),
    [userRoles]
  );

  const validateEmail = useCallback(
    (email: string): string => {
      if (!email) return 'Email is required';
      if (!EMAIL_REGEX.test(email)) return 'Invalid email format.';

      const isExistingMember = teamMembers?.registeredUsers.some(
        (member) => member.email.toLowerCase() === email.toLowerCase()
      );
      if (isExistingMember) return 'User already invited';

      const hasExistingInvitation = teamInvitations?.invitedUsers.some(
        (invitation) => invitation.email.toLowerCase() === email.toLowerCase()
      );
      if (hasExistingInvitation) return 'User already invited';

      return '';
    },
    [teamMembers, teamInvitations]
  );

  useEffect(() => {
    if (userRoles.length === 1) {
      setRole(userRoles[0]);
      return;
    }

    if (role && !userRoles.includes(role)) {
      setRole('');
    }
  }, [role, userRoles]);

  useEffect(() => {
    if (touchedEmail) {
      setEmailError(validateEmail(email));
    }
  }, [email, touchedEmail, validateEmail]);

  const validateCompany = useCallback((company: string): string => {
    if (!company.trim()) return 'Company is required';
    return '';
  }, []);

  useEffect(() => {
    if (touchedCompany) {
      setCompanyError(validateCompany(company));
    }
  }, [company, touchedCompany, validateCompany]);

  const isFormValid = useMemo(() => {
    return (
      validateEmail(email) === '' &&
      (!showCompanyInput || validateCompany(company) === '') &&
      role !== ''
    );
  }, [company, email, role, showCompanyInput, validateCompany, validateEmail]);

  const createRequestBody = (): InviteUserRequest | null => {
    if (!role) {
      return null;
    }

    return {
      email,
      role,
      ...(showCompanyInput ? { company: company.trim() } : {})
    };
  };

  const handleEmailBlur = () => {
    setTouchedEmail(true);
    const error = validateEmail(email);
    setEmailError(error);
  };

  const handleCompanyBlur = () => {
    setTouchedCompany(true);
    const error = validateCompany(company);
    setCompanyError(error);
  };

  const handleInviteUser = async () => {
    setTouchedEmail(true);
    const emailValidationError = validateEmail(email);
    setEmailError(emailValidationError);

    const companyValidationError = showCompanyInput
      ? validateCompany(company)
      : '';
    setTouchedCompany(!!showCompanyInput);
    setCompanyError(companyValidationError);

    if (!isFormValid || emailValidationError || companyValidationError) return;

    const inviteData = createRequestBody();
    if (!inviteData) return;

    dispatch(inviteUserStart());
    try {
      setIsOpen(false);
      await inviteUser(inviteData);
      dispatch(inviteUserSuccess());
      dispatch(setIsUserInvited(true));
    } catch (error) {
      console.error('Failed to send invite:', error);
      dispatch(inviteUserFailure());
    }
  };

  return (
    <div className="flex flex-col flex-start gap-6">
      <h2 className="text-lg leading-6 font-medium text-gray-900">
        Invite User
      </h2>

      <div className="flex flex-col w-full max-w-[740px] gap-6">
        <Input
          label="Email Address"
          placeholder="Enter email"
          id="email"
          type="email"
          required={true}
          value={email}
          maxLength={254}
          onBlur={() => handleEmailBlur()}
          onChange={(e) => {
            setEmail(e.target.value);
          }}
          error={touchedEmail ? emailError : ''}
          labelClassName="label"
          inputClassName="input"
        />
        {showCompanyInput && (
          <Input
            label="Company"
            placeholder="Enter company name"
            id="company"
            type="text"
            required={true}
            value={company}
            maxLength={254}
            onBlur={() => handleCompanyBlur()}
            onChange={(e) => {
              setCompany(e.target.value);
            }}
            error={touchedCompany ? companyError : ''}
            labelClassName="label"
            inputClassName="input"
          />
        )}

        <DropdownPortal
          label="Role"
          placeholder="Choose role"
          options={roleOptions}
          allOptions={roleOptions}
          value={role}
          disabled={userRoles.length <= 1}
          onChange={(value) => setRole(value as PlatformRole)}
        />
      </div>

      <div className="flex flex-row justify-end items-center gap-3">
        <WhiteButton onClick={() => setIsOpen(false)}>Cancel</WhiteButton>
        <PrimaryButton disabled={!isFormValid} onClick={handleInviteUser}>
          Invite User
        </PrimaryButton>
      </div>
    </div>
  );
}
